import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  Card,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Avatar,
  MenuItem,
  TextField,
  Pagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
} from "@mui/material";
import AppButton from "../../components/buyer/AppButton";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import {
  deleteOffers,
  fetchAllOffers,
  respondCounterOffer,
} from "../../features/buyer/buyerSlice";
import DeleteConfirmationModal from "../../components/seller/DeleteConfirmationModal";
import AppToast from "../../components/seller/AppToast";
import RejectionReasonModal from "../../components/seller/RejectionReasonModal";
import { capitalizeFirst } from "../../utils/stringUtils";
import AppLoader from "../../components/seller/AppLoader";
import CloseIcon from "@mui/icons-material/Close";
import theme from "../../styles/theme/theme";
import { MdOutlineLocalOffer } from "react-icons/md";
import { formatCurrency, formatPrice } from "../../utils/price";

const PAGE_SIZE = 10;

const MyOffers = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { list: offers = [], loading } = useSelector(
    (state) => state.buyer.offer
  );
  const { loading: responding, error: respondError } = useSelector(
    (state) => state.buyer.counterOffer
  );
  console.log("Offers:", offers);
  /* -------------------- UI STATE -------------------- */
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [sortBy, setSortBy] = useState("name");
  const [sortOrder, setSortOrder] = useState("asc");
  const [page, setPage] = useState(1);

  const [deleteModal, setDeleteModal] = useState({
    visible: false,
    product: null,
  });
  const [rejectionModal, setRejectionModal] = useState({
    visible: false,
    reason: "",
  });
  const [toastVisible, setToastVisible] = useState(false);
  const [toastType, setToastType] = useState("info");
  const [toastMessage, setToastMessage] = useState("");

  const [actionModal, setActionModal] = useState({
    open: false,
    type: null, // 'accept' | 'reject' | 'counter'
    offer: null,
  });

  const [note, setNote] = useState("");
  const [counterPrice, setCounterPrice] = useState("");
  const [counterDiscount, setCounterDiscount] = useState("");
  const [counterCurrency, setCounterCurrency] = useState("");
  const [errors, setErrors] = useState({});

  /* -------------------- FILTER + SORT -------------------- */
  const filteredOffers = useMemo(() => {
    let data = [...offers];

    // Search
    if (search) {
      const q = search.toLowerCase();
      data = data.filter(
        (o) =>
          o.id?.toLowerCase().includes(q) ||
          o.name?.toLowerCase().includes(q) ||
          o.category?.toLowerCase().includes(q) ||
          o.location?.toLowerCase().includes(q)
      );
    }

    // Status filter
    if (statusFilter !== "All") {
      data = data.filter((o) => o.status === statusFilter);
    }

    // Sorting
    data.sort((a, b) => {
      const aVal = a[sortBy];
      const bVal = b[sortBy];

      if (aVal == null) return 1;
      if (bVal == null) return -1;

      if (typeof aVal === "number") {
        return sortOrder === "asc" ? aVal - bVal : bVal - aVal;
      }

      return sortOrder === "asc"
        ? String(aVal).localeCompare(String(bVal))
        : String(bVal).localeCompare(String(aVal));
    });

    return data;
  }, [offers, search, statusFilter, sortBy, sortOrder]);

  /* -------------------- PAGINATION -------------------- */
  const totalPages = Math.ceil(filteredOffers.length / PAGE_SIZE);
  const paginatedOffers = filteredOffers.slice(
    (page - 1) * PAGE_SIZE,
    page * PAGE_SIZE
  );

  const fields = [
    "#",
    "Listing ID",
    "Image",
    "Name",
    "Category",
    "Subcategory",
    "Quantity",
    "Unit",
    "Max Buying Price (AUD)",
    // "Discount %",
    // "Discounted Price (AUD)",
    "Location",
    "Status",
    "Offer Status",
    "Counter Action",
    "Notes",
    "Actions",
  ];

  useEffect(() => {
    dispatch(fetchAllOffers())
      .unwrap()
      .catch((error) => {
        console.error("Failed to load offers", error);
        // Show toast or fallback UI
      });
  }, [dispatch]);

  // Check for toast message in navigation state
  useEffect(() => {
    if (location.state?.toast) {
      const { type, message } = location.state.toast;
      setToastType(type);
      setToastMessage(message);
      setToastVisible(true);

      // Clean up the state so it doesn't show again on refresh
      window.history.replaceState({}, document.title);
    }
  }, [location]);

  const confirmDelete = async () => {
    try {
      // console.log("Delete id:", deleteModal.product.id);
      const res = await dispatch(deleteOffers(deleteModal.offer.id)).unwrap();

      setDeleteModal({ visible: false, id: null });
      dispatch(fetchAllOffers());

      setToastType("success");
      setToastMessage(res?.message || "Product deleted successfully.");
      setToastVisible(true);
    } catch (err) {
      console.error("Failed to delete", err);
      setToastType("error");
      setToastMessage(err?.message || "Failed to delete product.");
      setToastVisible(true);
    }
  };

  // Status color map (use lowercase keys)
  const STATUS_COLORS = {
    approved: { bg: "#d4edda", color: "#155724" }, // green
    rejected: { bg: "#f8d7da", color: "#721c24" }, // red
    pending: { bg: "#fff3cd", color: "#856404" }, // yellow
    draft: { bg: "#e6f0ff", color: "#0b4a8f" }, // light blue
    default: { bg: "#f8f9fa", color: "#374151" }, // neutral
  };

  const validate = () => {
    const newErrors = {};

    if (actionModal.type === "counter" && !counterCurrency) {
      newErrors.counterCurrency = "Currency is required";
    }

    if (actionModal.type === "counter" && !counterPrice) {
      newErrors.counterPrice = "Counter price is required";
    }

    if (actionModal.type !== "accept" && !note.trim()) {
      newErrors.note = "Note is required";
    }

    if (actionModal.type === "counter") {
      const enteredDiscount = Number(counterDiscount);

      if (!counterDiscount) {
        newErrors.counterDiscount = "Discount % is required";
      } else if (enteredDiscount <= 0 || enteredDiscount > 100) {
        newErrors.counterDiscount = "Discount must be between 1 and 100";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const actionMap = {
    accept: "accept",
    reject: "reject",
    counter: "counter",
  };

  const handleSubmitAction = async () => {
    if (!validate()) return;

    const apiAction = actionMap[actionModal.type];

    const data = {
      action: apiAction,
      note: note || null,
    };

    if (actionModal.type === "counter") {
      data.price = Number(counterPrice);
      data.discount_percentage = Number(counterDiscount);
      data.currency = counterCurrency;
    }

    console.log("Action:", actionModal.type, data, actionModal.offer.id);

    try {
      await dispatch(
        respondCounterOffer({
          id: actionModal.offer.id,
          data,
        })
      ).unwrap();

      setToastType("success");
      setToastMessage(
        actionModal.type === "accept"
          ? "Offer accepted successfully"
          : actionModal.type === "reject"
            ? "Offer rejected successfully"
            : "Counter offer sent successfully"
      );
      setToastVisible(true);

      resetModal();
      dispatch(fetchAllOffers()); // refresh table
    } catch (err) {
      setToastType("danger");
      setToastMessage(err || "Something went wrong");
      setToastVisible(true);
    }
  };

  const resetModal = () => {
    setActionModal({ open: false, type: null, offer: null });
    setNote("");
    setCounterPrice("");
    setCounterDiscount("");
    setCounterCurrency("");
    setErrors({});
  };

  const tableActionButtonStyle = {
    padding: "4px 10px", // 👈 low vertical padding
    fontSize: "0.75rem",
    minHeight: "28px",
  };

  if (loading) {
    return <AppLoader />;
  }
  return (
    <>
      <Card
        sx={{
          p: { xs: 3, sm: 5 },
          mb: 8,
          borderRadius: 3,
          background: "#f9fff9",
          border: "1px solid rgba(0,0,0,0.05)",
          boxShadow: "0 4px 10px rgba(0,0,0,0.05)",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: 3,
            flexWrap: "wrap",
            gap: 2,
          }}
        >
          <Typography
            variant="h5"
            fontWeight={600}
            sx={{ fontSize: { xs: "1.2rem", sm: "1.5rem" } }}
          >
            My Offers
          </Typography>

          <AppButton
            fullWidth={false}
            variant="contained"
            onClick={() => navigate("/create-offer")}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              padding: "6px 12px",
            }}
          >
            <MdOutlineLocalOffer size={18} /> Create Offer
          </AppButton>
        </Box>

        {/* -------------------- CONTROLS -------------------- */}
        <Box
          sx={{
            display: "flex",
            gap: 2,
            mb: 3,
            flexWrap: "wrap",
          }}
        >
          <TextField
            label="Search"
            size="small"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
          />

          <TextField
            select
            size="small"
            label="Status"
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setPage(1);
            }}
          >
            {["All", "Pending", "Approved", "Rejected"].map((s) => (
              <MenuItem key={s} value={s}>
                {s}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            select
            size="small"
            label="Sort By"
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
          >
            <MenuItem value="name">Name</MenuItem>
            <MenuItem value="price">Price</MenuItem>
            <MenuItem value="quantity">Quantity</MenuItem>
          </TextField>

          <TextField
            select
            size="small"
            label="Order"
            value={sortOrder}
            onChange={(e) => setSortOrder(e.target.value)}
          >
            <MenuItem value="asc">Ascending</MenuItem>
            <MenuItem value="desc">Descending</MenuItem>
          </TextField>
        </Box>

        <Box sx={{ overflowX: "auto" }}>
          <Table sx={{ minWidth: 2000 }}>
            <TableHead>
              <TableRow>
                {fields.map((h) => (
                  <TableCell key={h} align="center" sx={{ fontWeight: 600 }}>
                    {h}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>

            <TableBody>
              {paginatedOffers.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={fields.length}
                    align="center"
                    sx={{
                      py: 6,
                      fontSize: "1rem",
                      fontWeight: 500,
                      color: "text.secondary",
                    }}
                  >
                    No offers found
                  </TableCell>
                </TableRow>
              ) : (
                paginatedOffers.map((offer, index) => {
                  //Get first seller offer PER ROW

                  const hasOffers =
                    Array.isArray(offer.offers) && offer.offers.length > 0;

                  const latestSellerOffer = hasOffers
                    ? offer.offers.filter(Boolean).slice(-1)[0] ?? null
                    : null;

                  const sellerStatusRaw =
                    latestSellerOffer?.status?.toLowerCase() || null;

                  const sellerStatus =
                    sellerStatusRaw === "seller_countered"
                      ? "countered"
                      : sellerStatusRaw;

                  const canAcceptReject =
                    hasOffers &&
                    (sellerStatus === "pending" ||
                      sellerStatus === "countered");

                  const canCounter = hasOffers && sellerStatus === "pending";

                  const hideAllActions =
                    !hasOffers ||
                    sellerStatus === "accepted" ||
                    sellerStatus === "rejected";

                  const itemStatus = offer.status?.toLowerCase();
                  const sellerColors =
                    STATUS_COLORS[sellerStatus] || STATUS_COLORS.default;

                  return (
                    <TableRow key={offer.id}>
                      <TableCell align="center">{index + 1}</TableCell>
                      <TableCell align="center" sx={{ minWidth: 140 }}>
                        {offer.listing_id}
                      </TableCell>

                      <TableCell align="center">
                        <Avatar
                          src={offer.image_url}
                          variant="rounded"
                          sx={{
                            width: 60,
                            height: 60,
                            mx: "auto",
                            objectFit: "cover",
                          }}
                        />
                      </TableCell>

                      <TableCell align="center" sx={{ minWidth: 200 }}>
                        {offer.name}
                      </TableCell>
                      <TableCell align="center" sx={{ minWidth: 160 }}>
                        {offer.category?.name}
                      </TableCell>
                      <TableCell align="center" sx={{ minWidth: 160 }}>
                        {offer.subcategory?.name}
                      </TableCell>
                      <TableCell align="center">{offer.quantity}</TableCell>
                      <TableCell align="center">
                        {offer.unit_of_measurement}
                      </TableCell>
                      {/* <TableCell align="center" sx={{ minWidth: 200 }}> */}
                      {/* A$ {formatPrice(offer.max_buying_price)} */}
                      {/* {formatCurrency(offer.max_buying_price, offer.currency)} */}
                      {/* </TableCell> */}
                      <TableCell align="center" sx={{ minWidth: 160 }}>
                        {offer.discount_percentage}
                      </TableCell>
                      {/* <TableCell align="center" sx={{ minWidth: 200 }}> */}
                      {/* A$ {formatPrice(offer.discounted_price)} */}
                      {/* {formatCurrency(offer.discounted_price, offer.currency)} */}
                      {/* </TableCell> */}

                      <TableCell align="center" sx={{ minWidth: 250 }}>
                        {offer.location}
                      </TableCell>

                      {/* ================= PRODUCT STATUS ================= */}
                      <TableCell align="center">
                        <Box
                          sx={{
                            px: 1.5,
                            py: 0.5,
                            borderRadius: 1,
                            fontWeight: 600,
                            display: "inline-block",
                            background:
                              STATUS_COLORS[offer.status]?.bg ||
                              STATUS_COLORS.default.bg,
                            color:
                              STATUS_COLORS[offer.status]?.color ||
                              STATUS_COLORS.default.color,
                          }}
                        >
                          {capitalizeFirst(offer.status)}
                        </Box>
                      </TableCell>

                      {/* ================= OFFER STATUS ================= */}
                      <TableCell align="center">
                        {sellerStatus ? (
                          <Box
                            sx={{
                              px: 1.5,
                              py: 0.5,
                              borderRadius: 1,
                              fontWeight: 600,
                              display: "inline-block",
                              background:
                                sellerStatus === "countered"
                                  ? "#fff3cd"
                                  : sellerColors.bg,
                              color:
                                sellerStatus === "countered"
                                  ? "#856404"
                                  : sellerColors.color,
                            }}
                          >
                            {capitalizeFirst(sellerStatus)}
                          </Box>
                        ) : (
                          "-"
                        )}
                      </TableCell>

                      {/* ================= COUNTER ACTION ================= */}
                      <TableCell align="center" sx={{ minWidth: 200 }}>
                        {!hideAllActions ? (
                          <Box
                            sx={{
                              display: "flex",
                              gap: 1,
                              justifyContent: "center",
                            }}
                          >
                            {canAcceptReject && (
                              <AppButton
                                variant="outlined"
                                fullWidth={false}
                                style={tableActionButtonStyle}
                                onClick={() =>
                                  setActionModal({
                                    open: true,
                                    type: "accept",
                                    offer: latestSellerOffer,
                                  })
                                }
                              >
                                Accept
                              </AppButton>
                            )}

                            {canAcceptReject && (
                              <AppButton
                                variant="outlined"
                                fullWidth={false}
                                style={{
                                  ...tableActionButtonStyle,
                                  borderColor: theme.palette.error.main,
                                  color: theme.palette.error.main,
                                }}
                                onClick={() =>
                                  setActionModal({
                                    open: true,
                                    type: "reject",
                                    offer: latestSellerOffer,
                                  })
                                }
                              >
                                Reject
                              </AppButton>
                            )}

                            {canCounter && (
                              <AppButton
                                variant="contained"
                                fullWidth={false}
                                style={{
                                  ...tableActionButtonStyle,
                                  backgroundColor: theme.palette.warning.main,
                                }}
                                onClick={() =>
                                  setActionModal({
                                    open: true,
                                    type: "counter",
                                    offer: latestSellerOffer,
                                  })
                                }
                              >
                                Counter
                              </AppButton>
                            )}
                          </Box>
                        ) : (
                          "-"
                        )}
                      </TableCell>

                      {/* ================= REJECTION ================= */}
                      <TableCell align="center" sx={{ minWidth: 250 }}>
                        {latestSellerOffer?.note ?? "-"}
                      </TableCell>

                      {/* ================= ACTIONS ================= */}
                      <TableCell align="center">
                        <Box
                          sx={{
                            display: "flex",
                            gap: 1,
                            justifyContent: "center",
                          }}
                        >
                          {itemStatus !== "approved" && (
                            <AppButton
                              variant="contained"
                              fullWidth={false}
                              style={tableActionButtonStyle}
                              onClick={() => navigate(`/add-offer/${offer.id}`)}
                            >
                              Edit
                            </AppButton>
                          )}
                          <AppButton
                            variant="outlined"
                            fullWidth={false}
                            style={tableActionButtonStyle}
                            onClick={() =>
                              setDeleteModal({ visible: true, offer })
                            }
                          >
                            Delete
                          </AppButton>
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </Box>

        {/* -------------------- PAGINATION -------------------- */}
        {totalPages > 1 && (
          <Box sx={{ mt: 3, display: "flex", justifyContent: "center" }}>
            <Pagination
              count={totalPages}
              page={page}
              onChange={(_, v) => setPage(v)}
              color="primary"
            />
          </Box>
        )}
      </Card>
      <Dialog
        open={actionModal.open}
        onClose={resetModal}
        fullWidth
        maxWidth="sm"
      >
        {/* Header */}
        <DialogTitle sx={{ display: "flex", justifyContent: "space-between" }}>
          <Typography variant="h6">
            {actionModal.type === "accept" && "Accept Offer"}
            {actionModal.type === "reject" && "Reject Offer"}
            {actionModal.type === "counter" && "Counter Offer"}
          </Typography>

          <IconButton onClick={resetModal}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        {/* Content */}
        <DialogContent dividers>
          <Typography color="text.secondary" mb={2}>
            {actionModal.type === "accept" &&
              "Are you sure you want to accept this offer?"}

            {actionModal.type === "reject" &&
              "Please provide a reason for rejecting this offer."}

            {actionModal.type === "counter" &&
              "Propose a counter offer with price and discount."}
          </Typography>

          {actionModal.type === "counter" && (
            <>
              {/* Max Buying Price */}
              <Box mb={2}>
                <Typography variant="caption" color="text.secondary">
                  Seller Offered Price
                </Typography>
                <Typography variant="subtitle1" fontWeight={600}>
                  {formatCurrency(actionModal.offer?.price)}
                </Typography>
              </Box>

              {/* Counter Currency */}
              <TextField
                fullWidth
                select
                label="Currency"
                value={counterCurrency}
                onChange={(e) => setCounterCurrency(e.target.value)}
                error={!!errors.counterCurrency}
                helperText={errors.counterCurrency}
                margin="dense"
              >
                <MenuItem value="">Select Currency</MenuItem>
                <MenuItem value="AUD">AUD</MenuItem>
                <MenuItem value="USD">USD</MenuItem>
              </TextField>

              {/* Counter Price */}
              <TextField
                fullWidth
                type="number"
                label="Counter Price"
                value={counterPrice}
                onChange={(e) => setCounterPrice(e.target.value)}
                error={!!errors.counterPrice}
                helperText={errors.counterPrice}
                margin="dense"
              />

              {/* Discount */}
              <TextField
                fullWidth
                type="number"
                label="Discount Percentage (%)"
                value={counterDiscount}
                onChange={(e) => setCounterDiscount(e.target.value)}
                error={!!errors.counterDiscount}
                helperText={errors.counterDiscount}
                margin="dense"
              />
            </>
          )}

          {/* Note */}
          <TextField
            fullWidth
            multiline
            rows={3}
            label={`Note ${actionModal.type === "accept" ? "(Optional)" : "*"}`}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            error={!!errors.note}
            helperText={errors.note}
            margin="dense"
          />
        </DialogContent>

        {/* Footer */}
        <DialogActions>
          <Button onClick={resetModal} color="inherit">
            Close
          </Button>

          <Button
            variant="contained"
            color={
              actionModal.type === "accept"
                ? "success"
                : actionModal.type === "reject"
                  ? "error"
                  : "warning"
            }
            onClick={handleSubmitAction}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      <DeleteConfirmationModal
        visible={deleteModal.visible}
        itemName={deleteModal.product?.name}
        onClose={() => setDeleteModal({ visible: false, product: null })}
        onConfirm={confirmDelete}
      />
      <AppToast
        visible={toastVisible}
        setVisible={setToastVisible}
        type={toastType}
        message={toastMessage}
      />
      <RejectionReasonModal
        visible={rejectionModal.visible}
        reason={rejectionModal.reason}
        onClose={() => setRejectionModal({ visible: false, reason: "" })}
      />
    </>
  );
};

export default MyOffers;
